// Canadian Football pitch drawer.
//
// Coordinates emitted by the backend (see
// libs/core/.../sports/canadian_football/pitch.ts):
//   P1..P15 = bottom sideline at y=0, x=-68.58..+68.58 (CFL fields are
//             110 yd of play + 20 yd end zones = ~137m long)
//   P16..P30 = top sideline at y=59.436, reversed x
//   P31..P34 = goalpost crossbars (z=3.048m) — used for goal-line marks
//
// Bottom P-keys run: P1 (-68.58) → P2 (-50.29) → P3 (-41.15) → P4 →
//   P5 → P6 → P7 (-4.57) → P8 (0, mid) → P9 (4.57) → P10 → P11 → P12 →
//   P13 → P14 (+50.29) → P15 (+68.58). Top P16..P30 mirror these.
//
// End zones are x=[-68.58..-50.29] (left) and x=[+50.29..+68.58] (right).
// Goal lines are at x=-50.29 and x=+50.29.
// Mid-field line is at x=0 (P8 ↔ P23).
import type { PitchCoordinates, WorldToCanvas } from '../../types'
import type { PitchDrawOptions } from '../types'
import { PITCH_FILL, PITCH_LINE, PITCH_LINE_WIDTH } from '../../constants'
import { HASH_LEN_YD } from '../american-football/constants'
import { YARD_M } from './constants'

export function drawCanadianFootballPitch(
  ctx: CanvasRenderingContext2D,
  pitch: PitchCoordinates,
  w2c: WorldToCanvas,
  canvasWidth: number,
  canvasHeight: number,
  options?: PitchDrawOptions
) {
  const fillColor = options?.fill ?? PITCH_FILL
  const lineColor = options?.line ?? PITCH_LINE
  const lineW = options?.lineWidth ?? PITCH_LINE_WIDTH

  const p = (key: string) => {
    const pt = pitch[key]
    if (!pt) return null
    return { ...pt, c: w2c(pt.x, pt.y) }
  }

  ctx.fillStyle = fillColor
  ctx.fillRect(0, 0, canvasWidth, canvasHeight)

  const corners = [p('P1'), p('P15'), p('P16'), p('P30')]
  if (corners.some((c) => !c)) return
  const [bl, br, tr, tl] = corners as NonNullable<(typeof corners)[number]>[]

  // End zone fills (x=[-68.58..-50.29] and x=[+50.29..+68.58])
  const p2 = p('P2')
  const p14 = p('P14')
  const p17 = p('P17')
  const p29 = p('P29')
  if (p2 && p29) {
    ctx.fillStyle = 'rgba(255, 255, 255, 0.04)'
    ctx.beginPath()
    ctx.moveTo(bl.c.cx, bl.c.cy)
    ctx.lineTo(p2.c.cx, p2.c.cy)
    ctx.lineTo(p29.c.cx, p29.c.cy)
    ctx.lineTo(tl.c.cx, tl.c.cy)
    ctx.closePath()
    ctx.fill()
  }
  if (p14 && p17) {
    ctx.fillStyle = 'rgba(255, 255, 255, 0.04)'
    ctx.beginPath()
    ctx.moveTo(p14.c.cx, p14.c.cy)
    ctx.lineTo(br.c.cx, br.c.cy)
    ctx.lineTo(tr.c.cx, tr.c.cy)
    ctx.lineTo(p17.c.cx, p17.c.cy)
    ctx.closePath()
    ctx.fill()
  }

  ctx.strokeStyle = lineColor
  ctx.lineWidth = lineW
  ctx.lineJoin = 'miter'

  // Outer boundary
  ctx.beginPath()
  ctx.moveTo(bl.c.cx, bl.c.cy)
  ctx.lineTo(br.c.cx, br.c.cy)
  ctx.lineTo(tr.c.cx, tr.c.cy)
  ctx.lineTo(tl.c.cx, tl.c.cy)
  ctx.closePath()
  ctx.stroke()

  // Yard-line pairs: each bottom P connects to its top-sideline mirror.
  // Skip the outer end-line corners (P1/P15 and P16/P30).
  const yardPairs: [string, string][] = [
    ['P2', 'P29'], // left goal line (x=-50.29)
    ['P3', 'P28'],
    ['P4', 'P27'],
    ['P5', 'P26'],
    ['P6', 'P25'],
    ['P7', 'P24'],
    ['P8', 'P23'], // centre line (x=0)
    ['P9', 'P22'],
    ['P10', 'P21'],
    ['P11', 'P20'],
    ['P12', 'P19'],
    ['P13', 'P18'],
    ['P14', 'P17'] // right goal line (x=+50.29)
  ]
  for (const [bKey, tKey] of yardPairs) {
    const b = p(bKey)
    const t = p(tKey)
    if (!b || !t) continue
    const isCentre = bKey === 'P8'
    const isGoalLine = bKey === 'P2' || bKey === 'P14'
    ctx.lineWidth = isCentre || isGoalLine ? lineW * 1.4 : lineW
    ctx.beginPath()
    ctx.moveTo(b.c.cx, b.c.cy)
    ctx.lineTo(t.c.cx, t.c.cy)
    ctx.stroke()
  }
  ctx.lineWidth = lineW

  // Hash marks — CFL puts them 24 yards from each sideline (so 11 yd from
  // centre). Pitch width is 59.436m (~65yd) — place two rows, at
  // y=24yd and y=(width-24yd).
  const pitchYMin = bl.y
  const pitchYMax = tl.y
  const hashYLow = pitchYMin + 24 * YARD_M
  const hashYHigh = pitchYMax - 24 * YARD_M
  const hashHalf = (HASH_LEN_YD * YARD_M) / 2

  const xStartYard = -50.292 // left goal line
  const xEndYard = 50.292 // right goal line
  const step = YARD_M
  for (let x = xStartYard; x <= xEndYard; x += step) {
    // Skip the major 5-yard lines (they're drawn by yardPairs already for
    // the ones we have; CFL core only emits 9-yard increments in pitch.ts
    // so the hashes fill in the missing per-yard marks).
    const rounded = Math.round(x / YARD_M)
    if (rounded % 5 === 0) continue
    for (const hashY of [hashYLow, hashYHigh]) {
      const top = w2c(x, hashY - hashHalf)
      const bot = w2c(x, hashY + hashHalf)
      ctx.beginPath()
      ctx.moveTo(top.cx, top.cy)
      ctx.lineTo(bot.cx, bot.cy)
      ctx.stroke()
    }
  }
}
